import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'How is my daily goal calculated?',
    answer:
      'Your daily water goal is calculated at 33ml per kilogram of body weight. For example, if you weigh 70kg, your daily goal will be 2,310ml (approximately 2.3 litres). This is based on general hydration guidelines and provides a good baseline for most adults.',
  },
  {
    question: 'Can I change my daily goal?',
    answer:
      'Your goal is automatically calculated from your body weight. To adjust your daily goal, go to Settings and update your weight. Your goal will recalculate immediately.',
  },
  {
    question: 'What are streaks?',
    answer:
      'Streaks track the number of consecutive days you meet your daily hydration goal. The longer your streak, the more consistent your hydration habits are becoming. Your current streak is displayed on the home screen.',
  },
  {
    question: 'What are streak freezes?',
    answer:
      'Streak freezes protect your streak on days when you do not meet your goal. Instead of breaking your streak, a freeze is automatically used to preserve it. Plus subscribers get 3 streak freezes per month, Pro subscribers get 10 per month, and free users do not have streak freezes. They reset at the beginning of each month.',
  },
  {
    question: 'What drink types can I track?',
    answer:
      'Free users can track water intake using preset cup sizes (250ml, 350ml, 500ml). Plus and Pro subscribers can also track different drink types including tea, coffee, juice, and more, with custom cup sizes.',
  },
  {
    question: 'How do I cancel my subscription?',
    answer:
      'On the web (Stripe): go to Settings > Manage Subscription within the app to open the Stripe Customer Portal where you can cancel or modify your subscription. On iOS (Apple): open your device Settings > Apple ID > Subscriptions, find Quench, and tap Cancel Subscription. Your access to paid features continues until the end of your current billing period.',
  },
  {
    question: 'Is my data backed up?',
    answer:
      'Yes. Your data is stored securely on Amazon Web Services (AWS) in the London (eu-west-2) region. All data is encrypted at rest and in transit. You do not need to manually back up your data — it is always synced and available when you sign in.',
  },
  {
    question: 'How do I delete my account?',
    answer:
      'Go to Settings > Delete Account. This will permanently delete your account and all associated data, including your water intake logs, weight, goals, streaks, and subscription records. This action cannot be undone.',
  },
  {
    question: 'The app is not syncing my data. What should I do?',
    answer:
      'Ensure you have an active internet connection. Try closing and reopening the app. If the issue persists, sign out and sign back in from Settings. If you continue to experience problems, please contact us.',
  },
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className={`faq__item ${isOpen ? 'faq__item--open' : ''}`} onClick={onClick}>
    <div className="faq__question">
      <h3>{question}</h3>
      <svg
        className="faq__chevron"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
    {isOpen && (
      <div className="faq__answer">
        <p>{answer}</p>
      </div>
    )}
  </div>
);

const QuenchSupport = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Support</span>
          <h1 className="legal-page__title">Quench Support</h1>
          <p className="legal-page__subtitle">
            Get help with Quench by Wolf Technologies Ltd
          </p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          {/* Contact Section */}
          <section className="support-contact">
            <div className="support-contact__card">
              <div className="support-contact__icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div>
                <h3>Need help?</h3>
                <p>
                  Contact us at{' '}
                  <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
                </p>
                <p className="support-contact__response">
                  We aim to respond to all enquiries within 48 hours.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="legal-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq__list">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => toggleFAQ(index)}
                />
              ))}
            </div>
          </section>

          {/* App Info */}
          <section className="legal-section">
            <h2>App Information</h2>
            <div className="support-info__grid">
              <div className="support-info__item">
                <span className="support-info__label">App</span>
                <span className="support-info__value">Quench</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Version</span>
                <span className="support-info__value">1.0</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Developer</span>
                <span className="support-info__value">Wolf Technologies Ltd</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Platform</span>
                <span className="support-info__value">iOS</span>
              </div>
            </div>
          </section>

          {/* Links */}
          <section className="legal-section">
            <h2>Useful Links</h2>
            <div className="support-links">
              <Link to="/quench/privacy" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Privacy Policy
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </Link>
              <a href="/" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                WolfTechnologies Home
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default QuenchSupport;
